/* import __COLOCATED_TEMPLATE__ from './contact-form.hbs'; */
import { action } from '@ember/object';
import { service } from '@ember/service';
import { waitForRender } from 'ember-simplepractice/utils/waiters';
import Component from '@glimmer/component';
import styles from './contact-form.module.scss';

const CLIENT_TYPES = [
  { type: 'Client', allowedType: 'client' },
  { type: 'ClientCouple', allowedType: 'couple' },
  { type: 'ClientContact', allowedType: 'contact' },
];

const CLIENT_TYPE_LABEL_KEY = 'booking.clientType';

const LABEL_SUFFIXES = {
  careCoordinatorMode: {
    client: 'careCoordinator.member',
    couple: 'careCoordinator.memberAndPartner',
    contact: 'careCoordinator.memberOnBehalf',
  },
  defaultMode: {
    client: 'me',
    couple: 'couple',
    contact: 'contact',
  },
};

export default class ContactContactForm extends Component {
  @service prescreener;

  styles = styles;

  @service session;

  get client() {
    return this.args.client;
  }

  get practice() {
    return this.args.practice;
  }

  get allowedClientTypes() {
    if (!this.practice) return [];

    if (!this.practice.get('featureContactFormCustomSettings')) {
      return ['client'];
    }

    return this.practice.get('clientTypesAllowedToBookAppt') || [];
  }

  get hasMultipleClientTypes() {
    return this.allowedClientTypes.length > 1;
  }

  get hasSingleClientType() {
    return this.allowedClientTypes.length === 1;
  }

  get singleAllowedType() {
    return this.hasSingleClientType ? this.allowedClientTypes[0] : null;
  }

  get clientTypeCollection() {
    let isCareCoordinator = this.prescreener.globalMonarchChannel?.careCoordinatorMode;
    let labelSuffix = isCareCoordinator
      ? LABEL_SUFFIXES.careCoordinatorMode
      : LABEL_SUFFIXES.defaultMode;

    return CLIENT_TYPES.map(clientType => {
      return {
        ...clientType,
        label: `${CLIENT_TYPE_LABEL_KEY}.${labelSuffix[clientType.allowedType]}`,
      };
    });
  }

  get clientTypes() {
    if (!this.practice) return [];
    return this.clientTypeCollection.filter(clientType =>
      this.allowedClientTypes.includes(clientType.allowedType)
    );
  }

  get autoSetClientType() {
    if (!this.hasSingleClientType) return null;
    const matchingType = this.clientTypeCollection.find(
      ct => ct.allowedType === this.singleAllowedType
    );
    return matchingType?.type || 'Client';
  }

  get showWhoReceivingCareSection() {
    return (
      this.practice &&
      this.practice.get('featureContactFormCustomSettings') &&
      this.hasMultipleClientTypes
    );
  }

  get showCoupleInfo() {
    if (!this.practice || !this.practice.get('featureContactFormCustomSettings')) return false;

    const isOnlyCouplesAllowed = this.hasSingleClientType && this.singleAllowedType === 'couple';
    const isClientCoupleSelected = this.client?.thisType === 'ClientCouple';

    return isOnlyCouplesAllowed || isClientCoupleSelected;
  }

  get showContactInfo() {
    if (!this.practice || !this.practice.get('featureContactFormCustomSettings')) return false;

    const isOnlyContactsAllowed = this.hasSingleClientType && this.singleAllowedType === 'contact';
    const isClientContactSelected = this.client?.thisType === 'ClientContact';

    return isOnlyContactsAllowed || isClientContactSelected;
  }

  get _relatedClient() {
    const relationships = this.client?.get('clientRelationships');
    return relationships?.get('firstObject.relatedClient');
  }

  get partner() {
    return this._relatedClient;
  }
  get clientModel() {
    return this._relatedClient;
  }

  get defaultClientTypeForCoupleAndContact() {
    return (
      this.allowedClientTypes.length === 2 &&
      this.allowedClientTypes.includes('couple') &&
      this.allowedClientTypes.includes('contact') &&
      !this.allowedClientTypes.includes('client')
    );
  }

  @action
  async initializeClientType() {
    const autoType =
      this.autoSetClientType || (this.defaultClientTypeForCoupleAndContact ? 'ClientCouple' : null);

    if (autoType && this.client) {
      await waitForRender();
      await this.onClientTypeChange(autoType);
    } else {
      await this._scheduleRelationshipCreation();
      this._updateValidationSettings();
    }
  }

  @action
  async onClientTypeChange(newType) {
    if (!this.client) return;

    this.client.thisType = newType;
    this.client.get('clientRelationships').clear();

    await waitForRender();
    this._createRelationshipsForType(newType);
    this.prescreener.validation.validateForClientType(newType);

    this._updateValidationSettings();
    this.args.onInputChange?.();
  }

  async _setAutoClientType() {
    const autoSetType = this.autoSetClientType;
    if (autoSetType && this.client) {
      await waitForRender();
      this.client.thisType = autoSetType;
    }
  }

  async _scheduleRelationshipCreation() {
    await waitForRender();
    this._createRelationshipsIfNeeded();
  }

  _createRelationshipsIfNeeded() {
    if (!this._needsToCreateRelationships()) return;

    if (this.showCoupleInfo) {
      this.createPartnerRelationship();
    } else if (this.showContactInfo) {
      this.createClientRelationship();
    }
  }

  _createRelationshipsForType(clientType) {
    const relationshipMap = {
      ClientCouple: () => this.createPartnerRelationship(),
      ClientContact: () => this.createClientRelationship(),
    };

    relationshipMap[clientType]?.();
  }

  _needsToCreateRelationships() {
    return this.client && !this.client.get('clientRelationships.length');
  }

  _createRelatedClient(modelType) {
    const store = this.client.store;
    return store.createRecord(modelType, {
      emails: [store.createRecord('email', { skipValidation: true })],
      phones: [store.createRecord('phone', { skipValidation: true })],
    });
  }

  _createAndAddRelationship(relatedClient) {
    const store = this.client.store;
    const relationship = store.createRecord('client-relationship', {
      relatedClient,
    });
    this.client.get('clientRelationships').pushObject(relationship);
  }

  createPartnerRelationship() {
    const partner = this._createRelatedClient('client-couple');
    this._createAndAddRelationship(partner);
  }

  createClientRelationship() {
    const clientModel = this._createRelatedClient('client-contact');
    this._createAndAddRelationship(clientModel);
  }

  _updateValidationSettings() {
    let client = this.client;
    if (!client) return;
    const relatedClient = client.get('clientRelationships')?.get('firstObject.relatedClient');
    this._legacySkipValidation(client, relatedClient);
    if (this.prescreener.featurePrescreenerV2) {
      this.prescreener.prescreenerV2SkipValidation(client, relatedClient);
    }
  }

  _legacySkipValidation(client, relatedClient) {
    const clientType = client.thisType;
    const shouldSkipValidation = this._shouldSkipValidation(clientType);

    client.set('skipContactFormSpecificValidations', shouldSkipValidation);

    relatedClient?.set(
      'skipContactFormSpecificValidations',
      !['ClientCouple', 'ClientContact'].includes(clientType)
    );
  }

  _shouldSkipValidation(clientType) {
    return (
      clientType === 'ClientContact' || (clientType !== 'Client' && clientType !== 'ClientCouple')
    );
  }
}
